import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getStoredToken } from '../services/auth';
import { getBlogTags } from '../services/api';
import RichTextEditor from './RichTextEditor';

interface BlogPost {
  id?: number;
  title: string;
  excerpt: string;
  content: string;
  tags: string[];
  is_published: boolean;
}

interface BlogEditorProps {
  mode: 'create' | 'edit';
}

const BlogEditor: React.FC<BlogEditorProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { isAuthenticated, isBlogger } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  const [post, setPost] = useState<BlogPost>({ title: '', excerpt: '', content: '', tags: [], is_published: false });
  const [tagInput, setTagInput] = useState('');
  const [allTags, setAllTags] = useState<string[]>([]);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [postLoaded, setPostLoaded] = useState(mode === 'create');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const tagInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (!isBlogger) { navigate('/'); return; }
    getBlogTags().then(res => { if (res.data?.tags) setAllTags(res.data.tags); });
    if (mode === 'edit' && slug) fetchPost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, slug, isAuthenticated, isBlogger]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node) &&
        tagInputRef.current && !tagInputRef.current.contains(e.target as Node)
      ) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const token = getStoredToken();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(`${API_URL}/api/blog/posts/${slug}`, { headers });
      if (!response.ok) throw new Error('Failed to fetch post');
      const data = await response.json();
      setPost({ title: data.title, excerpt: data.excerpt || '', content: data.content, tags: data.tags || [], is_published: data.is_published });
      setPostLoaded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent, publish: boolean = false) => {
    e.preventDefault();
    setError(null); setSuccess(null); setLoading(true);
    try {
      const url = mode === 'create' ? `${API_URL}/api/blog/posts` : `${API_URL}/api/blog/posts/${slug}`;
      const token = getStoredToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, {
        method: mode === 'create' ? 'POST' : 'PUT',
        headers,
        body: JSON.stringify({ ...post, is_published: publish, content: post.content || '<p></p>' }),
      });
      if (!response.ok) {
        const e = await response.json();
        const detail = e.detail;
        const msg = Array.isArray(detail)
          ? detail.map((d: { msg: string }) => d.msg).join('; ')
          : typeof detail === 'string' ? detail : 'Failed to save post';
        throw new Error(msg);
      }
      setSuccess(publish ? 'Post published successfully!' : 'Post saved as draft!');
      setTimeout(() => navigate('/admin/blog'), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save post');
    } finally {
      setLoading(false);
    }
  };

  const handleTagInputChange = (value: string) => {
    setTagInput(value);
    const available = allTags.filter(t => !post.tags.includes(t));
    if (value.trim()) {
      const lower = value.toLowerCase();
      const s = available.filter(t => t.toLowerCase().includes(lower));
      setTagSuggestions(s); setShowSuggestions(s.length > 0);
    } else {
      setTagSuggestions(available); setShowSuggestions(available.length > 0);
    }
  };

  const addTag = useCallback((tag: string) => {
    const trimmed = tag.trim();
    if (trimmed && !post.tags.includes(trimmed)) setPost(prev => ({ ...prev, tags: [...prev.tags, trimmed] }));
    setTagInput(''); setShowSuggestions(false);
    tagInputRef.current?.focus();
  }, [post.tags]);

  if (loading && mode === 'edit' && !postLoaded) return <div className="loading">Loading post...</div>;

  return (
    <div className="blog-editor-container">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">{mode === 'create' ? 'Create New Post' : 'Edit Post'}</h1>
        </div>

        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}

        <form className="blog-editor-form" onSubmit={(e) => handleSubmit(e, false)}>
          <div className="form-group">
            <label htmlFor="title" className="form-label">Title *</label>
            <input type="text" id="title" className="form-input" value={post.title}
              onChange={(e) => setPost({ ...post, title: e.target.value })} required placeholder="Enter post title" />
          </div>

          <div className="form-group">
            <label htmlFor="excerpt" className="form-label">Excerpt</label>
            <textarea id="excerpt" className="form-textarea" value={post.excerpt} rows={3}
              onChange={(e) => setPost({ ...post, excerpt: e.target.value })}
              placeholder="Brief summary of the post (optional)" />
            <small>Shown in the blog list preview</small>
          </div>

          <div className="form-group">
            <label className="form-label">Content *</label>
            {postLoaded && (
              <RichTextEditor
                key={slug || 'new'}
                content={post.content}
                onChange={(html) => setPost(prev => ({ ...prev, content: html }))}
                placeholder="Write your blog post content here..."
                minHeight={400}
              />
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Tags</label>
            <div style={{ position: 'relative' }}>
              <div className="tag-input-container">
                <input ref={tagInputRef} type="text" className="form-input" value={tagInput}
                  onChange={(e) => handleTagInputChange(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(tagInput); } if (e.key === 'Escape') setShowSuggestions(false); }}
                  onFocus={() => handleTagInputChange(tagInput)}
                  placeholder="Type a tag and press Enter" autoComplete="off" />
                <button type="button" className="btn btn-secondary" onClick={() => addTag(tagInput)} disabled={!tagInput.trim()}>Add</button>
              </div>
              {showSuggestions && (
                <div className="tag-suggestions" ref={suggestionsRef}>
                  {tagSuggestions.map(tag => (
                    <button key={tag} type="button" className="tag-suggestion-item"
                      onMouseDown={(e) => { e.preventDefault(); addTag(tag); }}>{tag}</button>
                  ))}
                </div>
              )}
            </div>
            {post.tags.length > 0 && (
              <div className="blog-tags" style={{ marginTop: '0.75rem' }}>
                {post.tags.map((tag) => (
                  <span key={tag} className="blog-tag" style={{ position: 'relative', paddingRight: '2rem' }}>
                    {tag}
                    <button type="button" onClick={() => setPost(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }))}
                      style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#666', fontSize: '1rem', lineHeight: 1 }}
                      title="Remove tag">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-secondary" disabled={loading}>{loading ? 'Saving...' : 'Save as Draft'}</button>
            <button type="button" className="btn btn-primary" onClick={(e) => handleSubmit(e, true)} disabled={loading}>{loading ? 'Publishing...' : 'Publish'}</button>
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/blog')} disabled={loading}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogEditor;
